import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { Tabs, type TabItem } from '../../components/shared/Tabs';
import { SubstitutePolicyForm } from './SubstitutePolicyForm';
import { SubstituteSettlementTab } from './SubstituteSettlementTab';
import '../../styles/settings-screen.css';
import './Substitute.css';

/**
 * 휴일대체 — 보상휴가(근기법 §57)를 회사가 정하는 자리 (2026-09-06 · 판 B → 판 C).
 *
 * ## 정본 P2-1 설정 화면
 * 화면머리(`PageHeader`) · **탭**(`Tabs` line) · 절 · 저장바 넷.
 *
 * ★★ **탭마다 «다른 내용»이다** — 정책은 회사가 **고치는 값**이고, 소멸 정산은 그 규칙이 낳은
 *    **결과를 보는 것**이다. 같은 것을 거르는 것이 아니라 다른 것을 담으므로 `line` 탭이다.
 * ★★ **반영 시점의 단위는 탭이다** — 정책 탭에는 저장바가 있고, 소멸 정산 탭에는 **없다**
 *    (고칠 것이 없는 읽기 화면). P2-1 이 그 가름을 이미 적어 두었다.
 * ★ **탭은 주소에 남긴다**(`?tab=`) — 새로고침 · 링크 · 뒤로가기가 들어야 한다.
 *   모르는 값은 첫 칸으로 떨어지므로 빈 화면이 안 생긴다.
 */

const TAB_KEYS = [
  { value: 'policy', label: '정책' },
  { value: 'settlement', label: '소멸 정산' },
] as const;

export function SubstitutePolicyPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab = TAB_KEYS.some((t) => t.value === raw) ? (raw as string) : 'policy';

  const pickTab = useCallback((next: string) => {
    // replace 로 바꾼다 — 탭을 여러 번 누르고 뒤로 가면 그만큼 눌러야 나가는 것을 막는다.
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    }, { replace: true });
  }, [setParams]);

  const items: TabItem[] = TAB_KEYS.map((t) => ({ value: t.value, label: t.label }));

  return (
    <div className="wc-set">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/substitute')}
        scope={scopeFor('/erp/attendance/substitute')}
        title="보상휴가"
        description="연장 · 야간 · 휴일근로를 수당 대신 휴가로 바꿀지 정합니다 (보상휴가 · 근기법 제57조)."
      />

      <Tabs
        className="wc-set__tabs"
        items={items}
        value={tab}
        onChange={pickTab}
        variant="line"
        ariaLabel="보상휴가 설정"
      />

      {tab === 'settlement' ? <SubstituteSettlementTab /> : <SubstitutePolicyForm />}
    </div>
  );
}
